// src/components/Admin/ChatManager/Chat/UserMessageStats.jsx
import { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Table, TableHead, TableBody,
    TableRow, TableCell, Button, Stack, CircularProgress, Divider,
} from '@mui/material';
import {
    LocalizationProvider,
    DatePicker,
} from '@mui/x-date-pickers';
import { koKR } from '@mui/x-date-pickers/locales';      // ✅ 경로 수정
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from '../../../../api/axios';

dayjs.locale('ko');

export default function UserMessageStats() {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);

    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs());

    /* ───── 데이터 로드 ────────────────────────────────── */
    const loadStats = useCallback(() => {
        setLoading(true);
        api.get('/api/chat/logs/user-stats/', {
            params: {
                start_date: startDate.format('YYYY-MM-DD'),
                end_date: endDate.format('YYYY-MM-DD'),
            },
        })
            .then(res => setStats(res.data.results || []))
            .catch(() => alert('데이터를 불러오지 못했습니다.'))
            .finally(() => setLoading(false));
    }, [startDate, endDate]);

    useEffect(loadStats, [loadStats]);

    /* ───── 엑셀 내보내기 ───────────────────────────────── */
    const exportToExcel = () => {
        const rows = stats.map(s => [s.username, s.message_count]);
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([
            ['아이디(닉네임)', '채팅 건수'],
            ...rows,
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'UserMessageStats');
        const blob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(
            new Blob([blob], { type: 'application/octet-stream' }),
            `user_message_stats_${Date.now()}.xlsx`,
        );
    };

    /* ───── 렌더 ───────────────────────────────────────── */
    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="ko"
            localeText={koKR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <Box sx={{ p: 3 }}>

                {/* 필터 바 */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        flexWrap="wrap"
                    >
                        {/* 날짜 선택 */}
                        <Stack direction="row" spacing={2}>
                            <DatePicker
                                label="시작일"
                                value={startDate}
                                onChange={v => setStartDate(v)}
                                format="YYYY-MM-DD"
                            />
                            <DatePicker
                                label="종료일"
                                value={endDate}
                                onChange={v => setEndDate(v)}
                                format="YYYY-MM-DD"
                            />
                        </Stack>

                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ display: { xs: 'none', md: 'block' } }}
                        />

                        {/* 버튼 묶음 */}
                        <Stack direction="row" spacing={2} sx={{ ml: { md: 'auto' } }}>
                            <Button variant="contained" onClick={loadStats}>
                                조회
                            </Button>
                            <Button variant="outlined" onClick={exportToExcel}>
                                엑셀로 저장
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {/* 데이터 테이블 */}
                <Paper sx={{ p: 2, overflowX: 'auto' }}>
                    {loading ? (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">아이디(닉네임)</TableCell>
                                    <TableCell align="center">채팅 건수</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            데이터가 없습니다
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    stats.map(s => (
                                        <TableRow key={s.username}>
                                            <TableCell align="center">{s.username}</TableCell>
                                            <TableCell align="center">{s.message_count}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Paper>
            </Box>
        </LocalizationProvider>
    );
}
